import React, { useCallback, useContext } from 'react';
import { TextInput, Select } from '@td-client-frontend/common';
import { Trash } from '@td-client-frontend/icons';
import { TemplateDirectiveConfig } from '../../../@types/Templates';
import { OperatorProps } from '../../../@types/TemplateOperators';
import { Default, Constant, Append, Replace, Column, Switch, TextFieldArg, Prepand } from './operators';
import { HeaderOption } from '../TemplateEntity';
import Reference from './operators/Reference';
import { TemplateEntityContext } from '../../../context/TemplateEntityContext';
import AdvancedSwitch from './operators/SwitchAdvanced';

interface Props {
  step: TemplateDirectiveConfig;
  index: number;
  onUpdate: (step: TemplateDirectiveConfig) => void;
  onRemove: () => void;
  dropDownOptions: HeaderOption[];
  preview: string;
  ignoreAdvanced?: boolean;
}

export interface StepArgsProps {
  step: TemplateDirectiveConfig;
  handleArgsChange: (value: string) => void;
  dropDownOptions: HeaderOption[];
  preview: string;
}

const operatorsData = {
  first: {
    label: 'span',
    subLabel: 'Number of characters to take from the start of input'
  },
  last: {
    label: 'span',
    subLabel: 'Number of characters to take from the end of input'
  },
  before: {
    label: 'term',
    subLabel: 'Take all characters from input up to the first occurrence of this'
  },
  after: {
    label: 'term',
    subLabel: 'Take all characters from input after the first occurrence of this'
  }
};

function Operator(props: OperatorProps) {
  const settingsComponents: Record<string, JSX.Element> = {
    column: <Column {...props} />,
    default: <Default {...props} />,
    constant: <Constant {...props} />,
    reference: <Reference {...props} />,
    prepend: <Prepand {...props} />,
    before: <TextFieldArg {...props} {...operatorsData.before} />,
    after: <TextFieldArg {...props} {...operatorsData.after} />,
    first: <TextFieldArg {...props} {...operatorsData.first} />,
    last: <TextFieldArg {...props} {...operatorsData.last} />,
    append: <Append {...props} />,
    switch: <Switch {...props} />,
    advanced_switch: <AdvancedSwitch {...props} />,
    replace: <Replace {...props} />
  };

  return settingsComponents[props.step.operation];
}

export function StepHolder({
  step,
  index,
  onUpdate,
  onRemove,
  preview,
  dropDownOptions,
  ignoreAdvanced = false
}: Props) {
  const {
    referenceData: { entityType = '' }
  } = useContext(TemplateEntityContext);
  const getStepOperations = useCallback(() => {
    const first = [
      { label: 'column', value: 'column' },
      { label: 'default', value: 'default' },
      { label: 'constant', value: 'constant' }
    ];
    if (['products'].includes(entityType)) {
      first.push({ label: 'reference', value: 'reference' });
    }
    const next = [
      { label: 'first', value: 'first' },
      { label: 'last', value: 'last' },
      { label: 'before', value: 'before' },
      { label: 'after', value: 'after' },
      { label: 'prepend', value: 'prepend' },
      { label: 'append', value: 'append' },
      { label: 'replace', value: 'replace' },
      { label: 'switch', value: 'switch' }
    ];
    if (!ignoreAdvanced) {
      next.push({ label: 'advanced switch', value: 'advanced_switch' });
    }
    return { first, next };
  }, [entityType, ignoreAdvanced]);

  const handleOperationChange = (val: string) => {
    step.operation = val;
    step.args = [];
    onUpdate(step);
  };

  const handleArgsChange = useCallback(
    (val) => {
      step.args = val;
      onUpdate(step);
    },
    [onUpdate, step]
  );

  return (
    <div className="flex flex-col mb-4 py-6 px-4 border-1 border-gray-default rounded-xl">
      <div className="flex flex-row justify-between mb-6 ">
        <h2 className="mr-2 ">Step {index + 1}</h2>
        {index > 0 && <RemoveBtn onRemove={onRemove} />}
      </div>
      <div className="flex flex-row justify-between mb-6 ">
        <div className="w-1/3">
          <Select
            options={index > 0 ? getStepOperations().next : getStepOperations().first}
            value={step.operation}
            placeholder="Operation"
            onChange={handleOperationChange}
            labelClasses="hiden"
            maxVisible={3}
          />
        </div>
        <div className="w-1/3">
          <TextInput fieldClasses="text-center" value={preview} disabled />
        </div>
      </div>
      <div className="flex flex-col justify-between mb-6 ">
        <h3>Settings</h3>
        {step.operation && (
          <Operator step={step} handleArgsChange={handleArgsChange} dropDownOptions={dropDownOptions} />
        )}
      </div>
    </div>
  );
}

function RemoveBtn({ onRemove }: { onRemove: () => void }) {
  return (
    <button
      onClick={onRemove}
      className="cursor-pointer focus:outline-none focus-visible:shadow-outline"
      type="button"
      aria-label="Close Modal"
      title="Close Modal"
    >
      <Trash className="fill-current text-primary-500" title="Remove file" height={60} width={60} />
    </button>
  );
}
